import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

type MsFamily = 'Outlined' | 'Rounded' | 'Sharp';

/** Utility class for handling material icons for now.
 * Currently only supports 24px, 400w icons.
 * See https://developers.google.com/fonts/docs/material_symbols for more details.
 * If we need to support other sizes or weights in the future, we can extend this class.
 * Also, if we need to support other icon families, we can extend the MsFamily type and the familyToCssName method.
 */
export class IconUtil {
  static ensureMaterialIcon24px400w<T>(fam: MsFamily, icons: T): void {
    const iconNames = Object.values(icons).sort(this.iconNameCompare).join(',');
    const famCss = this.familyToCssName(fam);
    const href = `https://fonts.googleapis.com/css2?family=${famCss}:opsz,wght,FILL,GRAD@24,400,0..1,0&icon_names=${iconNames}&display=block`;
    document.head.insertAdjacentHTML('beforeend', `<link href="${href}" rel="stylesheet">`);
  }

  /**
   * Register local SVG assets with MatIconRegistry for use with mat-icon
   * @param iconRegistry - Angular Material's MatIconRegistry service
   * @param domSanitizer - Angular's DomSanitizer service
   * @param icons - Object mapping icon names to asset file names
   * @param namespace - Optional namespace to group icons (default: 'assets')
   * @param basePath - Base path to assets (default: 'assets/')
   */
  static registerLocalSvgIcons(
    iconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    icons: Record<string, string>,
    namespace: string = 'assets',
    basePath: string = 'assets/'
  ): void {
    Object.entries(icons).forEach(([iconName, fileName]) => {
      const iconUrl = domSanitizer.bypassSecurityTrustResourceUrl(`${basePath}${fileName}`);
      iconRegistry.addSvgIconInNamespace(namespace, iconName, iconUrl);
    });
  }

  private static familyToCssName(f: MsFamily) {
    switch (f) {
      case 'Rounded':
        return 'Material+Symbols+Rounded';
      case 'Sharp':
        return 'Material+Symbols+Sharp';
      default:
        return 'Material+Symbols+Outlined';
    }
  }
  // Alphabetical, case-insensitive compare (stable locale-aware)
  private static iconNameCompare(a: string, b: string): number {
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  }
}
